import { useEffect, useRef, useState } from "react";

// A failed request keeps the data from the last one that succeeded.
const failedWith = (error) => (state) => ({ data: state.data, loading: false, error });

// Shared data fetching for /api/* calls, returning { data, loading, error }.
// The request is aborted when the component unmounts or the path changes, and an
// aborted request never updates state. Data from the previous path or poll stays
// in place until the next response arrives, as it did before this hook.
//
// select turns the parsed JSON into data. It runs when the response arrives, so
// if it throws, the hook returns the error and the component keeps showing its
// loader, which is what users saw when the copied fetch code threw.
//
// refreshInterval polls every that many ms once the first load has succeeded.
export const useApi = (path, { select, refreshInterval } = {}) => {
  const [state, setState] = useState({ data: undefined, loading: true, error: undefined });

  // The latest select, so callers can pass an inline function without refetching.
  const selectRef = useRef(select);
  useEffect(() => {
    selectRef.current = select;
  });

  useEffect(() => {
    let controller;
    let intervalId;

    const load = () => {
      if (controller) controller.abort();
      controller = new AbortController();
      const { signal } = controller;
      setState((s) => (s.loading ? s : { ...s, loading: true }));
      return fetch(path, { signal })
        .then((response) => response.json())
        .then((json) => (selectRef.current ? selectRef.current(json) : json))
        .then((data) => {
          if (signal.aborted) return false;
          setState({ data, loading: false, error: undefined });
          return true;
        })
        .catch((error) => {
          if (!signal.aborted) {
            // Logged so the failure still shows in the console and the smoke test, as the
            // uncaught rejection from the copied fetch code did.
            console.error(error);
            setState(failedWith(error));
          }
          return false;
        });
    };

    load().then((loaded) => {
      if (loaded && refreshInterval) intervalId = setInterval(load, refreshInterval);
    });

    return () => {
      controller.abort();
      clearInterval(intervalId);
    };
  }, [path, refreshInterval]);

  return state;
};
